import { atom, selector, selectorFamily } from 'recoil'
import { uniq } from 'lodash'
import { footfallClient, partnersClient, tradeAreaClient } from 'src/clients'
import {
  TradeAreaDateRange,
  DriveTimeTypes,
  CommonTradeAreaSection,
  ConsumerSpendRadiusData,
  NearVenue,
  SimpleVenueWithDistance,
  TrendsResponse,
  TrendsPeriod,
  CommonTradeAreaDetailsResponse,
  DriveTimeTradeAreaDetailsResponse,
  DriveTimeTradeAreaDetailsRequest,
  DriveTimeTradeAreaMapRequest,
  MultiPolygon,
  PromiseCache,
  BuildoutTradeAreaDetailsRequest,
  BBox2d,
  DemoRadiiResponse,
  Position,
  DriveTimeTransportationType,
} from 'src/common-services'
import { DEFAULT_LRU_SIZE, keyFactory, retryAtom, uncachedSelector, uncachedSelectorFamily } from 'src/utils/recoil'
import { googlePlace, venueMatch } from 'src/atoms/search'
import { ifNotFound } from 'src/utils/ajax'
import turf from 'src/utils/turf'
import { sqMToMiles } from 'src/utils/convert'
import { selectedCategory } from 'src/components/insights/performance-benchmarks/atoms'
import { trendDatesByPeriod } from 'src/components/insights/common/visits/atoms'
import { browserLogger } from 'src/utils/browser-logger'

import * as buildoutFrameAtoms from 'src/atoms/buildout-frame'
import * as atomsCompare from 'src/atoms/compare'
import * as atomsColors from 'src/atoms/colors'
import { computeHispanicPercentage } from 'src/atoms/demographics'

export const ALL_DEMOGRAPHICS_CATEGORIES = [
  'Overview',
  'Households',
  'Housing Units',
  'Gender',
  'Ethnicity',
  'Age',
  'Marital Status',
  'Value of Owner Occupied Housing Units',
  'Types of Housing Units',
  'Household Income',
  'Education',
  'Labor Force',
  'Transportation to Work',
  'Travel Time to Work',
  'Population by Industry',
  'Population by Occupation',
]

// Cached API Requests
const cacheGetTradeAreaDateRanges = new PromiseCache()
const cacheGetDriveTimeTradeAreaDetails = new PromiseCache()
const cacheGetDriveTimeNearby = new PromiseCache()
const cacheGetDriveTimeTradeAreaMap = new PromiseCache()

const key = keyFactory('drive-time-trade-area')

export enum TradeAreaDetailsTabs {
  OVERVIEW = 'OVERVIEW',
  CONSUMER = 'CONSUMER',
  DEMOGRAPHICS = 'DEMOGRAPHICS',
}

export const tab = atom<TradeAreaDetailsTabs>({
  key: key('tab'),
  default: TradeAreaDetailsTabs.OVERVIEW,
})

export const driveTimeTransportationType = atom<DriveTimeTransportationType>({
  key: key('drive-time-transportation-type'),
  default: DriveTimeTransportationType.DRIVING,
})

export const driveTimeOverlayRadius = selector<boolean>({
  key: key('drive-time-overlay-radius'),
  get: async ({ get }) => {
    const radiusRings = get(driveTimeOverlayRadiusRings)
    return radiusRings.some((x) => x > 0)
  },
})

export const driveTimeOverlayRadiusRings = atom<number[]>({
  key: key('drive-time-overlay-radius-rings'),
  default: [0, 0, 0],
})

export const driveTimeType = atom<DriveTimeTypes>({
  key: key('drive-time-type'),
  default: DriveTimeTypes.ARRIVAL,
})

export const driveTimeHour = atom<number>({
  key: key('drive-time-hour'),
  default: 6,
})

export const driveTimeDay = atom<number>({
  key: key('drive-time-day'),
  default: 0,
})

export const driveTimeDurations = atom<number[]>({
  key: key('drive-time-durations'),
  default: [5, 10, 15],
})

export const columnLabels = selector<string[]>({
  key: key('column-labels'),
  get: ({ get }) => {
    const durations = get(driveTimeDurations)
    return durations.map((dur) => `${dur} minutes`)
  },
})

export const ttaCenterPoint = atom<Position | undefined>({
  key: key('tta-center-point'),
  default: undefined,
})

export const ttaPosition = selector<Position | undefined>({
  key: key('tta-position'),
  get: ({ get }) => {
    const centerPoint = get(ttaCenterPoint)
    if (centerPoint) {
      return centerPoint
    }
    const place = get(googlePlace)
    return place?.geometry.location
  },
})

export const venue = uncachedSelector({
  key: key('venue'),
  get: async ({ get }) => {
    return get(venueMatch)
  },
  lru: DEFAULT_LRU_SIZE,
})

export const dates = selector<TradeAreaDateRange[]>({
  key: key('dates'),
  get: ({ get }) => {
    get(retryAtom)
    return ifNotFound(
      cacheGetTradeAreaDateRanges.get({}, () => tradeAreaClient.getTradeAreaDateRanges()),
      []
    )
  },
})

export const selectedDate = atom<number>({
  key: key('selected-date'),
  default: 0,
})

export const mapRequest = selector<DriveTimeTradeAreaMapRequest | BuildoutTradeAreaDetailsRequest | undefined>({
  key: key('map-request'),
  get: ({ get }) => {
    const buildoutToken = get(buildoutFrameAtoms.token)
    if (buildoutToken) {
      const configOverride = get(buildoutFrameAtoms.dataConfigOverride)
      const req: DriveTimeTradeAreaMapRequest | BuildoutTradeAreaDetailsRequest = {
        token: buildoutToken,
      }
      if (configOverride) {
        req.override = encodeURIComponent(JSON.stringify(configOverride))
      }
      return req
    }
    get(retryAtom)
    const position = get(ttaPosition)
    const venue = get(venueMatch)
    const datesValue = get(dates)
    const selectedDateValue = get(selectedDate)
    if (!position) {
      browserLogger.error('no place this should never happen')
      return undefined
    }
    const venueId = venue?.venueId || 'place'
    const hour = get(driveTimeHour)

    const [lng, lat] = position
    const req: DriveTimeTradeAreaMapRequest = {
      day: get(driveTimeDay),
      hour,
      durations: get(driveTimeDurations),
      type: get(driveTimeType),
      lat,
      lng,
      center: position,
      dateRange: datesValue[selectedDateValue],
      venueId,
      transportation: {
        type: get(driveTimeTransportationType),
      },
    }
    return req
  },
})

export const request = selector<DriveTimeTradeAreaDetailsRequest | BuildoutTradeAreaDetailsRequest | undefined>({
  key: key('request'),
  get: ({ get }) => {
    const buildoutToken = get(buildoutFrameAtoms.token)
    if (buildoutToken) {
      const configOverride = get(buildoutFrameAtoms.dataConfigOverride)
      const req: DriveTimeTradeAreaMapRequest | BuildoutTradeAreaDetailsRequest = {
        token: buildoutToken,
      }
      if (configOverride) {
        req.override = encodeURIComponent(JSON.stringify(configOverride))
      }
      return req
    }

    const _mapRequest = get(mapRequest)
    if (_mapRequest == null) {
      return undefined
    }
    return {
      ..._mapRequest,
      naicsCode: get(selectedCategory),
      transportation: {
        type: get(driveTimeTransportationType),
      },
    }
  },
})

export const tradeArea = selector<DriveTimeTradeAreaDetailsResponse | undefined>({
  key: key('trade-area'),
  get: ({ get }) => {
    const req = get(request)
    if (!req) {
      return undefined
    }
    if ('token' in req) {
      return cacheGetDriveTimeTradeAreaDetails.get(req, (args) =>
        partnersClient.getBuildoutDriveTimeTradeAreaDetails(args)
      )
    }
    return cacheGetDriveTimeTradeAreaDetails.get(req, (args) => tradeAreaClient.getDriveTimeTradeAreaDetails(args))
  },
})

export const hasDetails = selector<boolean>({
  key: key('has-details'),
  get: ({ get }) => {
    return get(tradeArea) != null
  },
})

export const mapLoaded = selectorFamily<boolean, boolean>({
  key: key('map-loaded'),
  get:
    (compare: boolean) =>
    ({ get }) => {
      return get(mapPolygons) != null
    },
})

const nearby = uncachedSelector<CommonTradeAreaDetailsResponse | undefined>({
  key: key('common-details'),
  get: ({ get }) => {
    const req = get(request)
    if (!req) {
      return undefined
    }
    return ifNotFound(
      cacheGetDriveTimeNearby.get(req as DriveTimeTradeAreaDetailsRequest, (args) =>
        tradeAreaClient.getDriveTimeNearby(args)
      ),
      undefined
    )
  },
  lru: DEFAULT_LRU_SIZE,
})

const tradeAreaSections = uncachedSelector<CommonTradeAreaSection[]>({
  key: key('common-sections'),
  get: async ({ get }) => {
    return get(tradeArea)?.sections ?? []
  },
  lru: DEFAULT_LRU_SIZE,
})

export const demographicsExpandedCategories = atom<string[]>({
  key: key('demographics-expanded-categories'),
  default: ['Overview', 'Households'],
})

export const demographicsData = uncachedSelector<DemoRadiiResponse | undefined>({
  key: key('demographics-data'),
  get: async ({ get }) => {
    const tradeAreas = get(tradeAreaSections)
    const durations = get(driveTimeDurations)
    return tradeAreas.map((area, i) => {
      const data = computeHispanicPercentage(area.demographics)
      return {
        data,
        consumerSpendData: area.consumerSpend,
        radius: durations[i],
      }
    })
  },
  lru: DEFAULT_LRU_SIZE,
})

export const venuesInTradeArea = uncachedSelector<NearVenue[]>({
  key: key('nearby-venues'),
  get: ({ get }) => {
    const match = get(venueMatch)
    // this will never be the case here
    if (!match) {
      return []
    }
    const data = get(nearby)
    const venues = data?.nearbyVenues ?? []
    return venues.filter((x) => x.venueId !== match.venueId)
  },
  lru: DEFAULT_LRU_SIZE,
})

export const locationsInTradeArea = uncachedSelector<SimpleVenueWithDistance[]>({
  key: key('locations-in-trade-area'),
  get: ({ get }) => {
    const data = get(nearby)
    return data?.nearbyLocations ?? []
  },
  lru: DEFAULT_LRU_SIZE,
})

export const nearbyLocationCounts = uncachedSelectorFamily<Array<number | undefined>, boolean>({
  key: key('nearby-simple-venues'),
  get:
    (compare: boolean) =>
    ({ get }) => {
      const data = get(tradeAreaSections)
      return data.map((x) => x.nearbyLocationCount)
    },
  lru: DEFAULT_LRU_SIZE,
})

type DemoOverview = {
  currentYear: number | undefined
  fiveYear: number | undefined
}

type View = {
  format: string
  name: string
  values: DemoOverview[]
}

type DemoOverviewRange = View[]

export const demoOverview = uncachedSelectorFamily<DemoOverviewRange | undefined, boolean>({
  key: key('demo-overview'),
  get:
    (compare: boolean) =>
    ({ get }) => {
      const demographics = get(demographicsData)
      const consumerSpend = get(consumerSpendData(compare))
      if (!demographics || !consumerSpend) {
        return undefined
      }
      const result: DemoOverviewRange = []
      const totalPop = demographics.map((item) => {
        const { currentYear, fiveYear } = item.data.Overview.Population
        return { currentYear, fiveYear }
      })
      result.push({
        format: 'number',
        name: 'Total Population',
        values: totalPop,
      })

      const workdayPop = demographics.map((item) => {
        const { currentYear, fiveYear } = item.data.Overview['Workday Population']
        return { currentYear, fiveYear }
      })
      result.push({
        format: 'number',
        name: 'Workday Population',
        values: workdayPop,
      })

      const households = demographics.map((item) => {
        const { currentYear, fiveYear } = item.data.Households['Total Households']
        return { currentYear, fiveYear }
      })
      result.push({
        format: 'number',
        name: 'Total Households',
        values: households,
      })

      const income = demographics.map((item) => {
        const { currentYear, fiveYear } = item.data['Household Income']['Average Household Income']
        return { currentYear, fiveYear }
      })
      result.push({
        format: 'currency',
        name: 'Avg. Household Income',
        values: income,
      })

      const spend = consumerSpend.map((item) => {
        const currentYear = item.currentYear.total
        const fiveYear = item.fiveYear.total
        return { currentYear, fiveYear }
      })
      result.push({
        format: 'currency',
        name: 'Total Consumer Spend',
        values: spend,
      })

      return result
    },
  lru: DEFAULT_LRU_SIZE,
})

export const consumerSpendData = uncachedSelectorFamily<ConsumerSpendRadiusData[] | undefined, boolean>({
  key: key('spend-data'),
  get:
    (compare: boolean) =>
    async ({ get }) => {
      const tradeAreas = get(tradeAreaSections)
      return tradeAreas.map((area) => {
        return area.consumerSpend
      })
    },
  lru: DEFAULT_LRU_SIZE,
})

export const mapPolygons = selector<MultiPolygon[]>({
  key: key('map-polygons'),
  get: async ({ get }) => {
    const req = get(mapRequest)
    const durations = get(driveTimeDurations)
    if (req == null) {
      return []
    }
    if ('token' in req) {
      const sections = await ifNotFound(partnersClient.getBuildoutDriveTimeTradeAreaMap(req), [])
      const results = durations.map((time) => sections.find((section) => section.driveTime === time)?.polygon) as any
      return results.filter((result?: any) => !!result)
    }
    const sections = await ifNotFound(
      cacheGetDriveTimeTradeAreaMap.get(req, (args) => tradeAreaClient.getDriveTimeTradeAreaMap(args)),
      []
    )
    const results = durations.map((time) => sections.find((section) => section.driveTime === time)?.polygon) as any
    return results
  },
})

export const largestMapPolygon = selector<MultiPolygon>({
  key: key('largest-map-polygon'),
  get: async ({ get }) => {
    const polygons = get(mapPolygons)
    let largestAreaPolygon: MultiPolygon = polygons[0]
    for (const poly of polygons) {
      const currentLargestArea = turf.area(largestAreaPolygon)
      const currentArea = turf.area(poly)
      if (currentArea > currentLargestArea) {
        largestAreaPolygon = poly
      }
    }
    return largestAreaPolygon
  },
})

export const boundingBox = selector<BBox2d | undefined>({
  key: key('bounding-box'),
  get: async ({ get }) => {
    const polygons = get(mapPolygons)
    if (polygons.length === 0) {
      return
    }
    const largestAreaPolygon = get(largestMapPolygon)
    return turf.bbox(largestAreaPolygon) as BBox2d
  },
})

export const areas = selector<number[]>({
  key: key('areas'),
  get: async ({ get }) => {
    const polygons = get(mapPolygons)
    const areas: number[] = []

    for (const poly of polygons) {
      const area = turf.area(poly)
      areas.push(sqMToMiles(area))
    }

    return areas
  },
})

export const sizeMiles = selector<Array<number | null>>({
  key: key('size-miles'),
  get: ({ get }) => {
    const durations = get(mapPolygons)
    if (!durations) {
      return []
    }
    const areas = durations.map((section) => turf.area(section)).map((x) => sqMToMiles(x))
    return areas
  },
})

export const demographicsCategories = selector<string[]>({
  key: key('demographics-categories'),
  get: ({ get }) => {
    const breakdown = get(demographicsData)
    if (!breakdown) {
      return []
    }
    const keys = breakdown.flatMap((x) => Object.keys(x.data))
    return uniq(keys)
  },
})

const monthTrendsForVenue = uncachedSelectorFamily<TrendsResponse | undefined, string>({
  key: key('trend-for-venue'),
  get:
    (venueId) =>
    ({ get }) => {
      const months = get(trendsChartMonths)
      const start = months[0]
      const end = months[months.length - 1]
      if (!start || !end) {
        return undefined
      }
      return ifNotFound(
        footfallClient.getTrends({
          start,
          end,
          period: TrendsPeriod.MONTHLY,
          venueId,
        }),
        undefined
      )
    },
  lru: DEFAULT_LRU_SIZE,
})

export const trendsChartMonths = selector<string[]>({
  key: key('trends-chart-categories'),
  get: ({ get }) => {
    const dates = get(trendDatesByPeriod(TrendsPeriod.MONTHLY)).periods
    const months = dates.slice(-12)
    if (months.length < 3) {
      return []
    }
    return months
  },
})

export type TrendsChartSeriesData = {
  name: string
  data: number[]
  color?: string
}

export const trendsChartData = selector<TrendsChartSeriesData[]>({
  key: key('trends-chart-data'),
  get: ({ get }) => {
    const venue = get(venueMatch)
    if (!venue) {
      return []
    }
    const venues = get(venuesInTradeArea)
    const nearby = venues.slice(0, 4)
    const ids = [venue.venueId].concat(nearby.map((x) => x.venueId))
    const venueTrends = ids.map((id) => get(monthTrendsForVenue(id)))
    const results: TrendsChartSeriesData[] = []
    for (let i = 0; i < venueTrends.length; i++) {
      const res = venueTrends[i]
      if (res == null) {
        continue
      }
      const name = i === 0 ? venue.brand ?? 'venue' : venues[i - 1].brand

      results.push({
        name,
        data: res.map((x) => x.personCountSum),
      })
    }
    return results
  },
})

export const trendsChartDataCompare = selector<TrendsChartSeriesData[]>({
  key: key('trends-chart-data-compare'),
  get: ({ get }) => {
    const colors = get(atomsColors.gradientsTopColors)
    const venue = get(venueMatch)
    if (!venue) {
      return []
    }
    const extraVenues = get(atomsCompare.venues)
    const extraVenuesIds: string[] = extraVenues.flatMap((x) => (x ? [x.venueId] : []))
    const ids = [venue.venueId, ...extraVenuesIds]
    const venueTrends = ids.map((id) => get(monthTrendsForVenue(id)))
    const results: TrendsChartSeriesData[] = []
    for (let i = 0; i < venueTrends.length; i++) {
      const res = venueTrends[i]
      if (res == null) {
        continue
      }
      const name = i === 0 ? venue.brand ?? 'venue' : extraVenues[i - 1]?.brand || '-'
      results.push({
        name,
        data: res.map((x) => x.personCountSum),
        color: colors[i],
      })
    }
    return results
  },
})

export const showTradeAreaSize = atom<boolean>({
  key: key('show-trade-area-size'),
  default: true,
})

export const showSameCategory = atom<boolean>({
  key: key('show-same-category'),
  default: true,
})

export const showDemographicOverview = atom<boolean>({
  key: key('show-demographic-Overview'),
  default: true,
})

export const showCoverageByDistance = atom<boolean>({
  key: key('show-coverage-by-distance'),
  default: true,
})

export const showTradeAreaOverlap = atom<boolean>({
  key: key('show-trade-area-overlap'),
  default: true,
})

export const demographicsDisplayCategories = atom<string[]>({
  key: key('demographics-display-categories'),
  default: ALL_DEMOGRAPHICS_CATEGORIES,
})

export const showConsumerSpend = atom<boolean>({
  key: key('show-consumer-spend'),
  default: true,
})

export const showWithinComparison = atom<boolean>({
  key: key('show-whithin-comparison'),
  default: true,
})

export const showNearestCategory = atom<boolean>({
  key: key('show-nearest-category'),
  default: true,
})

export const showTrendComparisonChart = atom<boolean>({
  key: key('show-trend-comparison-chart'),
  default: true,
})
